import { useEffect, useRef, useState } from "react";

type InlineEditProps = {
  value: string | null | undefined;
  onSave: (v: string) => unknown | Promise<unknown>;
  type?: "text" | "select" | "textarea" | "number" | "url";
  options?: string[];
  placeholder?: string;
  /** Shown in the accent color when empty, instead of the placeholder; the placeholder then hints inside the input. */
  addLabel?: string;
  label?: string;
  displayClassName?: string;
  inputClassName?: string;
};

export function InlineEdit({
  value,
  onSave,
  type = "text",
  options,
  placeholder,
  addLabel,
  label,
  displayClassName,
  inputClassName,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const inFlight = useRef(false);
  const inputRef = useRef<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >(null);
  const fieldName = label || placeholder || "value";
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [saved]);

  const save = async () => {
    if (inFlight.current) return;
    if (draft === (value || "")) {
      setEditing(false);
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError("");
    try {
      const result = onSave(draft);
      await result;
      setSaved(result instanceof Promise);
      setEditing(false);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Could not save. Try again.",
      );
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  if (!editing)
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        <button
          type="button"
          aria-label={`Edit ${fieldName}: ${value || "empty"}`}
          onClick={() => {
            setDraft(value || "");
            setError("");
            setSaved(false);
            setEditing(true);
          }}
          className={`${displayClassName ?? "hover:bg-primary/5 px-1 -mx-1 rounded transition-colors text-sm"} text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring`}
        >
          {value || (addLabel
            ? <span className="font-semibold text-primary-text">{addLabel}</span>
            : <span className="text-muted-foreground italic">{placeholder || "—"}</span>)}
        </button>
        {saved && (
          <span
            role="status"
            className="text-green-700 dark:text-green-400 text-xs"
          >
            Saved
          </span>
        )}
      </span>
    );
  const shared = {
    value: draft,
    disabled: saving,
    "aria-label": fieldName,
    "aria-invalid": !!error,
    onChange: (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => setDraft(event.target.value),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === "Escape" && !saving) {
        event.preventDefault();
        setEditing(false);
      }
      if (event.key === "Enter" && type !== "textarea") {
        event.preventDefault();
        void save();
      }
    },
    className:
      inputClassName ??
      "text-sm border rounded px-2 py-1 bg-background focus:ring-2 focus:ring-ring outline-none w-full min-w-0",
  };
  return (
    <span
      className="inline-flex flex-wrap items-center gap-1 w-full min-w-0"
      aria-busy={saving}
    >
      {type === "select" ? (
        <select {...shared} ref={inputRef as React.Ref<HTMLSelectElement>}>
          <option value="">—</option>
          {options?.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          {...shared}
          placeholder={addLabel ? placeholder : undefined}
          ref={inputRef as React.Ref<HTMLTextAreaElement>}
        />
      ) : (
        <input
          {...shared}
          placeholder={addLabel ? placeholder : undefined}
          type={type}
          ref={inputRef as React.Ref<HTMLInputElement>}
        />
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => void save()}
        className="rounded border px-2 py-1 text-xs focus-visible:ring-2 focus-visible:ring-ring"
      >
        {saving ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        disabled={saving}
        onClick={() => setEditing(false)}
        className="rounded px-2 py-1 text-xs focus-visible:ring-2 focus-visible:ring-ring"
      >
        Cancel
      </button>
      {error && (
        <span role="alert" className="basis-full text-sm text-destructive">
          {error}
        </span>
      )}
    </span>
  );
}
